/**
 * Client for the Zebra Browser Print agent (CROCS-968).
 *
 * The agent is a small application installed on the workstation that exposes
 * locally connected Zebra printers (USB) over 127.0.0.1. On the first request
 * from a new origin the agent shows a dialog asking the user to allow that
 * origin; until accepted, requests fail.
 *
 * From an https page the agent is reached over https://127.0.0.1:9101 (the
 * agent installs a local certificate for this), matching the behavior of
 * Zebra's official BrowserPrint JS library; plain http pages use port 9100.
 */

const AGENT_BASE_URL =
  typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? 'https://127.0.0.1:9101'
    : 'http://127.0.0.1:9100'

const STATUS_TIMEOUT_MS = 3000
const WRITE_TIMEOUT_MS = 10000

export interface ZebraDevice {
  name: string
  uid: string
  connection: string
  deviceType: string
  version?: number | string
  provider?: string
  manufacturer?: string
}

export enum BROWSER_PRINT_ERROR {
  /** Agent not installed/running, or this origin has not been accepted yet */
  AGENT_UNAVAILABLE = 'AGENT_UNAVAILABLE',
  /** Agent is running but no (default) printer is connected */
  NO_PRINTER = 'NO_PRINTER',
  /** Sending the label to the printer failed */
  WRITE_FAILED = 'WRITE_FAILED',
}

export class BrowserPrintError extends Error {
  public readonly code: BROWSER_PRINT_ERROR

  constructor(code: BROWSER_PRINT_ERROR, message: string) {
    super(message)
    this.name = 'BrowserPrintError'
    this.code = code
  }
}

// Plain text keeps the request "simple" so the agent never needs a CORS preflight.
const request = async (
  path: string,
  options: { method?: string; body?: string; timeoutMs?: number } = {},
): Promise<Response> => {
  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? STATUS_TIMEOUT_MS,
  )

  try {
    return await fetch(`${AGENT_BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      body: options.body,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      signal: controller.signal,
    })
  } catch (error) {
    throw new BrowserPrintError(
      BROWSER_PRINT_ERROR.AGENT_UNAVAILABLE,
      `Browser Print agent is not reachable on ${AGENT_BASE_URL}`,
    )
  } finally {
    clearTimeout(timeout)
  }
}

export const isAgentAvailable = async (): Promise<boolean> => {
  try {
    const response = await request('/available')
    return response.ok
  } catch (error) {
    return false
  }
}

export const getAvailablePrinters = async (): Promise<ZebraDevice[]> => {
  const response = await request('/available')

  if (!response.ok) {
    throw new BrowserPrintError(
      BROWSER_PRINT_ERROR.AGENT_UNAVAILABLE,
      `Browser Print agent responded with status ${response.status}`,
    )
  }

  const payload = await response.json()
  return payload?.printer ?? []
}

export const getDefaultPrinter = async (): Promise<ZebraDevice | null> => {
  const response = await request('/default?type=printer')

  if (!response.ok) {
    throw new BrowserPrintError(
      BROWSER_PRINT_ERROR.AGENT_UNAVAILABLE,
      `Browser Print agent responded with status ${response.status}`,
    )
  }

  const body = await response.text()
  if (!body) {
    return null
  }

  try {
    const device: ZebraDevice = JSON.parse(body)
    return device?.uid ? device : null
  } catch (error) {
    return null
  }
}

export const printZpl = async (
  zpl: string,
  device?: ZebraDevice,
): Promise<void> => {
  const printer = device ?? (await getDefaultPrinter())

  if (!printer) {
    throw new BrowserPrintError(
      BROWSER_PRINT_ERROR.NO_PRINTER,
      'No Zebra printer is connected to the Browser Print agent',
    )
  }

  const response = await request('/write', {
    method: 'POST',
    body: JSON.stringify({ device: printer, data: zpl }),
    timeoutMs: WRITE_TIMEOUT_MS,
  })

  if (!response.ok) {
    throw new BrowserPrintError(
      BROWSER_PRINT_ERROR.WRITE_FAILED,
      `Printing failed with status ${response.status}`,
    )
  }
}
